import type { IsoDate } from '../../lib/dates';

/** Shapes returned by /api/timesheets/*. Kept in one place for the page and its dialogs. */

export interface Entry {
    readonly id: number;
    readonly staffId: number | null;
    readonly staffName: string | null;
    readonly jobId: number | null;
    readonly clientId: number | null;
    readonly clientName: string | null;
    readonly jobNumber: number | null;
    readonly jobAddress: string | null;
    readonly jobSuburb: string | null;
    readonly jobOther: string | null;
    readonly workDate: IsoDate;
    readonly startTime: string | null;
    readonly finishTime: string | null;
    readonly lunchTaken: boolean;
    readonly totalHours: number | null;
    readonly overtime15: number;
    readonly overtime2: number;
    readonly leaveHours: number;
    readonly leaveType: string | null;
    /** The employee's clock-off note. */
    readonly note: string | null;
    readonly isOpen: boolean;
    readonly invoiced: boolean;
    readonly isManualEntry: boolean;
    /** Non-null = an admin set these hours. calculatedHours is what the clock gave. */
    readonly adjustedHours: number | null;
    readonly calculatedHours: number | null;
    readonly adjustedReason: string | null;
    readonly editedByName: string | null;
    readonly editedAt: string | null;
    readonly leaveRequestId: number | null;
}

export interface TimesheetStaff {
    readonly staffId: number;
    readonly name: string;
    readonly incomeType: string | null;
    readonly xeroLinked: boolean;
    readonly payrollKinds: readonly string[];
}

export interface Lookups {
    readonly clients: readonly { readonly id: number; readonly name: string }[];
    readonly jobs: readonly {
        readonly id: number;
        readonly jobNumber: number | null;
        readonly address: string | null;
        readonly suburb: string | null;
        readonly clientId: number | null;
    }[];
}

export interface PayNote {
    readonly id: number;
    readonly staffId: number;
    readonly noteDate: IsoDate;
    readonly note: string;
    readonly updatedBy: string | null;
    readonly updatedAt: string;
}

export interface PayrollCell {
    readonly date: IsoDate;
    readonly computed: number;
    readonly override: number | null;
    readonly value: number;
}

export interface PayrollRow {
    readonly kind: string;
    readonly name: string;
    readonly cells: readonly PayrollCell[];
    readonly total: number;
    readonly countsTowardTotal: boolean;
}

export interface PayrollPeriod {
    readonly staff: {
        readonly staffId: number;
        readonly name: string;
        readonly incomeType: string | null;
        readonly xeroLinked: boolean;
    };
    readonly from: IsoDate;
    readonly to: IsoDate;
    readonly dates: readonly IsoDate[];
    readonly rows: readonly PayrollRow[];
    readonly dayTotals: readonly number[];
    readonly grandTotal: number;
    readonly notes: readonly PayNote[];
    readonly upload: XeroUpload | null;
    /** Every send for this fortnight, newest first. Re-sending appends. */
    readonly uploads: readonly XeroUpload[];
    /** The live sign-off, or null when this fortnight has not been approved. */
    readonly approval: PayrollApproval | null;
    /** Every approval and withdrawal, newest first. */
    readonly approvalHistory: readonly PayrollApprovalEvent[];
    readonly xero: { readonly connected: boolean; readonly payrollEnabled: boolean };
}

export interface XeroUpload {
    readonly uploadedAt: string;
    readonly uploadedBy: string | null;
    readonly xeroTimesheetId?: string | null;
}

export interface PayrollApproval {
    readonly approvedAt: string;
    readonly approvedBy: string | null;
    /** The total at the moment of sign-off, so a later edit cannot rewrite it. */
    readonly approvedTotalHours: number | null;
}

export interface PayrollApprovalEvent extends PayrollApproval {
    readonly withdrawnAt: string | null;
    readonly withdrawnBy: string | null;
}

export interface LeaveRequest {
    readonly id: number;
    readonly staffId: number;
    readonly staffName: string | null;
    readonly leaveType: string;
    readonly startsOn: IsoDate;
    readonly endsOn: IsoDate;
    readonly status: 'pending' | 'approved' | 'rejected';
    readonly requestedAt: string;
    readonly decidedAt: string | null;
    readonly decidedBy: string | null;
}

/**
 * The legacy "Other" list (timesheet_others), in its order, plus RDO.
 * Choosing one makes the row leave rather than job work.
 */
export const LEAVE_TYPES = [
    { id: 'trade_school', label: 'Trade School' },
    { id: 'sick_leave', label: 'Sick Leave' },
    { id: 'annual_leave', label: 'Annual Leave' },
    { id: 'public_holiday', label: 'Public Holiday' },
    { id: 'unpaid_leave', label: 'Unpaid Leave' },
    { id: 'compassionate_leave', label: 'Compassionate Leave' },
    { id: 'rdo', label: 'RDO' },
] as const;

export function leaveLabel(id: string | null): string {
    return LEAVE_TYPES.find((l) => l.id === id)?.label ?? id ?? '';
}

/** "7.50", or a dash for nothing. */
export function hrs(n: number | null | undefined): string {
    return n === null || n === undefined || n === 0 ? '—' : n.toFixed(2);
}

/** DD/MM/YYYY. */
export function dmy(iso: string): string {
    return `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
}

/** "Mon 14/09", the legacy day-header format. */
export function dayLabel(iso: IsoDate): string {
    const dow = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][new Date(`${iso}T00:00:00Z`).getUTCDay()] ?? '';
    return `${dow} ${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
}
